import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

const breadcrumbItems: BreadcrumbItem[] = [
  { name: '首页', path: '/' },
  { name: '关于我们' },
];

const faqs = [
  {
    question: '叮当漫画网的漫画都是免费的吗？',
    answer: '是的！叮当漫画网上所有漫画资源均为免费阅读，无需付费、无需会员，让每一位漫画爱好者都能畅享阅读乐趣。',
  },
  {
    question: '什么是"无删减"版本？',
    answer: '无删减版本指的是漫画内容完整呈现，不对任何情节、画面进行删减或修改，保留原作者的全部创作内容，给您最原汁原味的阅读体验。',
  },
  {
    question: '大黄丫头漫画多久更新一次？',
    answer: '大黄丫头漫画保持稳定的更新频率，通常每周更新2-3话。具体更新时间请关注网站首页的最新更新栏目。',
  },
  {
    question: '如何在手机上阅读漫画？',
    answer: '叮当漫画网采用响应式设计，完美适配手机、平板和电脑。您只需在手机浏览器中打开我们的网站，即可享受流畅的下拉式阅读体验，无需下载任何APP。',
  },
  {
    question: '漫画支持离线下载吗？',
    answer: '目前叮当漫画网专注于在线阅读体验，暂不支持离线下载功能。但我们提供流畅的在线阅读体验，只要您有网络连接，就可以随时随地畅快追漫。',
  },
  {
    question: '如何联系我们或有版权问题？',
    answer: '您可以通过本页面的联系表单与我们取得联系。如有版权相关问题，请在留言中详细说明，我们会在24小时内回复处理。',
  },
];

const testimonials = [
  {
    name: '小猫咪爱吃鱼',
    avatar: 'https://readdy.ai/api/search-image?query=A%20cute%20anime%20style%20girl%20avatar%20with%20warm%20peach%20background%2C%20minimalist%20portrait%2C%20clean%20design&width=100&height=100&seq=avatar-01&orientation=squarish',
    rating: 5,
    text: '叮当漫画网的大黄丫头太好看啦！画风超棒，剧情也很感人。下拉式阅读体验特别好，一口气看了二十多话根本停不下来！',
    date: '2026-07-28',
  },
  {
    name: '追风少年',
    avatar: 'https://readdy.ai/api/search-image?query=A%20cool%20anime%20style%20boy%20avatar%20with%20warm%20amber%20background%2C%20minimalist%20portrait%2C%20clean%20design&width=100&height=100&seq=avatar-02&orientation=squarish',
    rating: 5,
    text: '无删减真的很重要！之前在其他平台看总觉得少了什么，来叮当漫画网之后才发现原来有这么多精彩内容。已推荐给所有朋友！',
    date: '2026-07-25',
  },
  {
    name: '甜甜的奶茶',
    avatar: 'https://readdy.ai/api/search-image?query=A%20sweet%20anime%20style%20girl%20avatar%20with%20soft%20pink%20background%2C%20minimalist%20portrait%2C%20clean%20design&width=100&height=100&seq=avatar-03&orientation=squarish',
    rating: 5,
    text: '每天都来看有没有更新，真的是我追漫的首选平台！分类也很全，除了大黄丫头还发现了好多好看的漫画，简直是宝藏网站！',
    date: '2026-07-20',
  },
];

export default function AboutPage() {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setFormMessage('您的留言已成功发送，我们会尽快回复！');
      setFormState({ name: '', email: '', message: '' });
      return;
    }
    setFormStatus('submitting');
    try {
      const body = new URLSearchParams({
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        message: formData.get('message') as string,
      }).toString();
      const res = await fetch('https://readdy.ai/api/form/d9ota5qftmtnfcfh0dl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* raw */ }
      const code = (parsed as { code?: string })?.code;
      if (res.ok && code === 'OK') {
        setFormStatus('success');
        setFormMessage('您的留言已成功发送，我们会尽快回复！');
        setFormState({ name: '', email: '', message: '' });
      } else {
        const msg = (parsed as { meta?: { message?: string }; message?: string })?.meta?.message
          || (parsed as { message?: string })?.message
          || text;
        throw new Error(String(msg));
      }
    } catch (err: unknown) {
      setFormStatus('error');
      const msg = err instanceof Error ? err.message : '发送失败，请稍后再试';
      setFormMessage(msg.includes('spam') ? '发送失败，请稍后再试' : msg);
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于叮当漫画网',
    description: '叮当漫画网 - 大黄丫头漫画无删减下拉式免费阅读平台',
    url: 'https://dingdangmh.com/about',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 tracking-wide">
              关于叮当漫画网
            </h1>
            <p className="text-sm text-foreground-500 mt-1">大黄丫头漫画 - 无删减下拉式免费阅读平台</p>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" aria-labelledby="brand-title">
          <div className="max-w-3xl mx-auto">
            <h2 id="brand-title" className="text-xl md:text-2xl font-heading text-foreground-900 tracking-wide text-center mb-8">
              品牌介绍
            </h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                <strong className="text-foreground-800">叮当漫画网</strong>成立于2020年，是国内领先的在线漫画免费阅读平台。我们致力于为广大漫画爱好者提供<strong>优质、完整、免费</strong>的漫画阅读体验。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                作为<strong>大黄丫头漫画</strong>的官方合作平台，我们为读者提供<strong>无删减版本</strong>的完整内容。所有漫画采用创新的<strong>下拉式阅读</strong>方式，无需翻页，让您沉浸在连续的阅读体验中。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                我们的漫画库涵盖<strong>热血、搞笑、恋爱、奇幻、悬疑、校园、武侠、科幻</strong>等八大分类，拥有超过10,000部漫画资源，每日持续更新。无论是热门大作还是小众精品，您都能在叮当漫画网找到心仪的作品。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                叮当漫画网的使命是<strong className="text-primary-600">让每一个人都能免费享受漫画带来的快乐</strong>。我们相信，好的内容不应该被付费墙阻隔。无论您身在何处，只要打开叮当漫画网，就能畅享最优质的漫画阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/70">
                <p className="text-2xl font-bold text-primary-500 font-heading">2020</p>
                <p className="text-xs text-foreground-500 mt-1">创立年份</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/70">
                <p className="text-2xl font-bold text-accent-500 font-heading">10,000+</p>
                <p className="text-xs text-foreground-500 mt-1">漫画资源</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center border border-background-200/70">
                <p className="text-2xl font-bold text-secondary-500 font-heading">500万+</p>
                <p className="text-xs text-foreground-500 mt-1">活跃读者</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="w-full bg-background-100 py-12 md:py-16" aria-labelledby="reputation-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="reputation-title" className="text-xl md:text-2xl font-heading text-foreground-900 tracking-wide text-center mb-8">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, j) => (
                          <span key={j} className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-xs text-accent-500"></i>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.text}</p>
                  <p className="text-xs text-foreground-400 mt-3">{t.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" aria-labelledby="faq-title">
          <div className="max-w-3xl mx-auto">
            <h2 id="faq-title" className="text-xl md:text-2xl font-heading text-foreground-900 tracking-wide text-center mb-8">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h3 className="text-sm font-semibold text-foreground-800 pr-4">{faq.question}</h3>
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-200 group-open:rotate-45">
                      <i className="ri-add-line text-foreground-400"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full bg-background-100 py-12 md:py-16" aria-labelledby="contact-title">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 id="contact-title" className="text-xl md:text-2xl font-heading text-foreground-900 tracking-wide text-center mb-8">
              联系我们
            </h2>
            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
              <form onSubmit={handleSubmit} data-readdy-form>
                <div className="spm-field-wrap">
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formState.name}
                      onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
                      required
                      placeholder="请输入您的姓名"
                      className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱地址 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formState.email}
                      onChange={(e) => setFormState((s) => ({ ...s, email: e.target.value }))}
                      required
                      placeholder="请输入您的邮箱"
                      className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formState.message}
                      onChange={(e) => setFormState((s) => ({ ...s, message: e.target.value }))}
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请输入您的留言（最多500字）"
                      className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formState.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-primary-600 text-center">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-accent-600 text-center">{formMessage}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}